import type { ReactNode } from "react";
import {
  DashboardIcon,
  MapPinIcon,
  TruckIcon,
  RouteIcon,
  DriverIcon,
  BellIcon,
  GeofenceIcon,
  MaintenanceIcon,
  FuelIcon,
  ReportsIcon,
  GroupsIcon,
  DeviceIcon,
  ImportsIcon,
  RulesIcon,
  CalendarIcon,
  SupportIcon,
  TelegramIcon,
  ShieldIcon,
  RbacIcon,
  ArchiveIcon,
  IntelligenceIcon,
  AutomationIcon,
  PlugIcon,
  AppStoreIcon,
  GooglePlayIcon,
} from "./NavIcons.js";

/**
 * GT-NAV-IA-DOMAIN-GROUPING-FUEL-COSTS-GENIUS-FAMILY-01 (DL-124) — the single
 * source of truth for the App Shell navigation information architecture.
 *
 * This evolves the DL-122 `Operate / Control / More / Admin` model into the
 * owner-approved (#870) **domain-based fleet command structure**:
 *
 *   - **Pinned daily-cockpit items** (`GENIUS_INTELLIGENCE_ITEM` + `PINNED_ITEMS`)
 *     render at the top and are always visible.
 *   - **Domain groups** (`NAV_GROUPS`: Genius / Fleet / Monitoring / Fuel & Costs /
 *     Maintenance / Reports / Integrations / Administration) render as collapsible
 *     accordions in the desktop sidebar and the mobile drawer.
 *   - **Footer items** (`FOOTER_ITEMS`: Support) render as standalone links at the
 *     bottom for all roles (no clean domain home).
 *   - **Mobile bottom nav** (`MOBILE_BOTTOM_ITEMS`) mirrors the pinned cockpit
 *     (Genius · Dashboard · Map · Trips) + a "More" trigger that opens the drawer.
 *
 * Hard rule (DL-122 + owner): this model **preserves every route the prior nav
 * exposed** — nothing is removed or hidden. Items that have no real destination yet
 * are rendered as honest, badged **preview** items (no broken links, no placeholder
 * pages). The one new destination is the polished `GeniusAutomationsPage` Coming Soon
 * page (`/genius/automations`).
 *
 * Ausnahme seit GT-OPEN-154.3 (Owner-Entscheidung 10): der alte
 * `/insights`-Eintrag ("Question Library", DL-121) ist entfernt, weil die
 * Seite geloescht wurde. `/insights` existiert nur noch als Weiterleitung
 * auf `/intelligence` — also kein Ziel fuer die Navigation.
 */

/**
 * `route` — a real client route (`to`). `preview` — a future capability with no route
 * yet: rendered as a disabled, badged item (honest "Soon", never a broken link).
 * `external` — an out-link (`href`) that leaves the web app (new tab).
 */
export type NavItemKind = "route" | "preview" | "external";

export interface NavItem {
  /** Stable identity used for React keys, test ids, and accordion state. */
  readonly id: string;
  readonly labelKey: `nav.${string}`;
  readonly Icon: () => ReactNode;
  /** Defaults to `"route"`. */
  readonly kind?: NavItemKind;
  /** The client route (kind `"route"`). */
  readonly to?: string;
  /** The external URL (kind `"external"`). */
  readonly href?: string;
  /**
   * When true the active state matches the path exactly (react-router `end`).
   * Used where a sibling nav item is a sub-path (e.g. `/maintenance` next to
   * `/maintenance/recurring`, `/admin-hub` next to `/admin-hub/roles`).
   */
  readonly exact?: boolean;
  /** Optional tooltip / description (e.g. "AI-generated fleet reports"). */
  readonly descriptionKey?: `nav.${string}`;
  /** Optional badge (e.g. "Soon" for preview items). */
  readonly badgeKey?: `nav.${string}`;
  /**
   * Paket 003707 — a full badge graphic (kind `"external"` only), e.g. the
   * official App Store / Google Play store badges. Rendered in place of the
   * icon + label when the sidebar is expanded; collapsed falls back to
   * `Icon` + a tooltip so the item still has an accessible, compact form.
   */
  readonly badgeImage?: { readonly src: string };
}

export interface NavGroup {
  readonly id: string;
  readonly labelKey: `nav.group.${string}`;
  readonly Icon: () => ReactNode;
  readonly items: ReadonlyArray<NavItem>;
  /** Nur fuer Verwaltende: siehe `showAdmin` in `AppShell.tsx` (A-W4-WEB1). */
  readonly adminOnly?: boolean;
}

export interface PrimaryNavItem extends NavItem {
  readonly subtitleKey: `nav.${string}`;
}

/** A bottom-nav item always has a real route. */
export type BottomNavItem = NavItem & { readonly to: string };

/**
 * C-GTCC-LOGIN-SEP1 — there is no Platform Admin Console out-link here any more.
 *
 * The console used to be a second SPA on THIS origin (`/admin/`), so the sidebar
 * could link to it same-origin. The Owner decision of 2026-09-05 (point 13) moved
 * it to its own hostname (`{$GTCC1_DOMAIN}`) and the `/admin/*` route is gone from
 * the customer site block (`infra/compose/app/edge/Caddyfile`). The customer bundle
 * deliberately does not learn the platform hostname: keeping platform staff off the
 * customer origin is the point of the separation, and a hardcoded cross-origin link
 * would be a second place that has to agree with the edge.
 */

/**
 * The flagship AI entry. DL-121 canonical visible name **"Genius Intelligence"** with
 * the positioning subtitle **"Fleet AI Command Center"**. Pinned above the groups — the
 * single AI focal moment in the shell; never hidden inside the Genius group.
 */
export const GENIUS_INTELLIGENCE_ITEM: PrimaryNavItem = {
  id: "intelligence",
  to: "/intelligence",
  labelKey: "nav.genius_intelligence",
  subtitleKey: "nav.genius_intelligence.subtitle",
  Icon: IntelligenceIcon,
};

/**
 * Pinned daily-cockpit items — always visible, never inside an accordion. The owner's
 * direction: Genius Intelligence (above) · Dashboard · Live Map · Trips are the
 * everyday operations entry points. (Vehicles is important but less frequent than Map /
 * Trips, so it moves under Fleet.)
 */
export const PINNED_ITEMS: ReadonlyArray<NavItem> = [
  { id: "dashboard", to: "/dashboard", labelKey: "nav.dashboard", Icon: DashboardIcon },
  GENIUS_INTELLIGENCE_ITEM,
  { id: "map", to: "/map", labelKey: "nav.map", Icon: MapPinIcon },
  { id: "trips", to: "/trips", labelKey: "nav.trips", Icon: RouteIcon },
];

export const NAV_GROUPS: ReadonlyArray<NavGroup> = [
  {
    id: "genius",
    labelKey: "nav.group.genius",
    Icon: IntelligenceIcon,
    items: [
      // Paket 003702: Genius Intelligence also opens the Genius group as its
      // first entry (same target as the pinned flagship above); the pin stays.
      // Own `id` (not the pinned item's) so both can render at once without
      // colliding on the same `app-sidebar-item-*` test id.
      {
        id: "genius-intelligence",
        to: "/intelligence",
        labelKey: GENIUS_INTELLIGENCE_ITEM.labelKey,
        Icon: GENIUS_INTELLIGENCE_ITEM.Icon,
      },
      // Paket 003702: Genius Reports is NOT Genius Intelligence — its own
      // honest Coming Soon page (no backend), same pattern as Automations.
      {
        id: "genius-reports",
        to: "/genius/reports",
        labelKey: "nav.genius_reports",
        descriptionKey: "nav.genius_reports.description",
        badgeKey: "nav.badge.soon",
        Icon: ReportsIcon,
      },
      // Approved future product family — a polished Coming Soon page (no backend).
      {
        id: "genius-automations",
        to: "/genius/automations",
        labelKey: "nav.genius_automations",
        descriptionKey: "nav.genius_automations.description",
        badgeKey: "nav.badge.soon",
        Icon: AutomationIcon,
      },
      // GT-OPEN-154.3: der Eintrag "Fragenbibliothek" (`/insights`) ist
      // entfernt, weil die alte Seite geloescht ist. `/insights` leitet nur
      // noch auf `/intelligence` weiter und gehoert daher nicht in die Navigation.
    ],
  },
  {
    id: "fleet",
    labelKey: "nav.group.fleet",
    Icon: TruckIcon,
    items: [
      { id: "vehicles", to: "/vehicles", labelKey: "nav.vehicles", Icon: TruckIcon },
      { id: "devices", to: "/devices", labelKey: "nav.devices", Icon: DeviceIcon },
      { id: "drivers", to: "/drivers", labelKey: "nav.drivers", Icon: DriverIcon },
      { id: "groups", to: "/groups", labelKey: "nav.vehicle_groups", Icon: GroupsIcon },
      { id: "imports", to: "/imports", labelKey: "nav.fleet_imports", Icon: ImportsIcon },
    ],
  },
  {
    id: "monitoring",
    labelKey: "nav.group.monitoring",
    Icon: BellIcon,
    items: [
      { id: "alerts", to: "/alerts", labelKey: "nav.alerts", Icon: BellIcon, exact: true },
      { id: "geofences", to: "/geofences", labelKey: "nav.geofences", Icon: GeofenceIcon },
      { id: "rules", to: "/rules", labelKey: "nav.rules", Icon: RulesIcon, exact: true },
      // GT-CUSTOMER-NOTIFICATIONS-NAV-ALERTS-CLARITY-01 (PR-C1) — "Rules
      // registry" (/rules-registry) is a coverage/reference catalog, not a
      // day-to-day customer surface. Removed from the customer Monitoring nav
      // per owner. The ROUTE stays registered in App.tsx so it remains
      // reachable by direct URL / for admin-debug; only the nav entry is gone.
      // GT-NOTIFICATION-SETTINGS-CHANNEL-FIRST-REDESIGN-01 — the personal
      // "Notification Settings" page (`/me/notifications`) is NOT a fleet
      // monitoring surface, so it no longer sits here. It is reached from
      // the Account menu (account/settings path). The page stays reachable
      // via the Account menu and the Integrations → Telegram connector.
    ],
  },
  {
    // Fuel is strategically important and gets its own first-class domain (not under
    // Maintenance). Four items are real today; the rest are honest "Soon" previews.
    id: "fuel",
    labelKey: "nav.group.fuel_costs",
    Icon: FuelIcon,
    items: [
      { id: "fuel-events", to: "/fuel/events", labelKey: "nav.fuel_events", Icon: FuelIcon },
      { id: "fuel-costs", to: "/fuel/costs", labelKey: "nav.fuel_costs", Icon: FuelIcon },
      {
        id: "fuel-consumption",
        to: "/fuel/consumption",
        labelKey: "nav.fuel_consumption",
        Icon: FuelIcon,
      },
      {
        // A-FUEL-SUSPICIOUS-UI1 — the review queue ("Prüffall") behind this
        // entry is real now: page, filters, detail and the review-case
        // endpoints from D-FUEL-REVIEW-CASES1. The "Soon" badge came off
        // with the data, not before it. The label stays deliberately
        // observational; the entry leads to cases to look at, never to a
        // theft finding (DL-197 §5).
        id: "fuel-theft",
        to: "/fuel/review-cases",
        labelKey: "nav.fuel_theft",
        Icon: FuelIcon,
      },
      {
        id: "fuel-reports",
        to: "/reports?group=fuel",
        labelKey: "nav.fuel_reports",
        Icon: ReportsIcon,
      },
      {
        id: "cost-reports",
        to: "/reports?group=cost",
        labelKey: "nav.cost_reports",
        Icon: ReportsIcon,
      },
    ],
  },
  {
    id: "maintenance",
    labelKey: "nav.group.maintenance",
    Icon: MaintenanceIcon,
    items: [
      {
        id: "maintenance",
        to: "/maintenance",
        labelKey: "nav.maintenance",
        Icon: MaintenanceIcon,
        exact: true,
      },
      { id: "calendar", to: "/calendar", labelKey: "nav.calendar", Icon: CalendarIcon },
      {
        id: "maintenance-recurring",
        to: "/maintenance/recurring",
        labelKey: "nav.maintenance.recurring",
        Icon: MaintenanceIcon,
      },
      {
        id: "maintenance-templates",
        to: "/maintenance/templates",
        labelKey: "nav.maintenance.templates",
        Icon: MaintenanceIcon,
      },
    ],
  },
  {
    // Cross-domain / global reporting. Domain-specific reports (fuel/cost) live under
    // Fuel & Costs; AI reports live under Genius.
    id: "reports",
    labelKey: "nav.group.reports",
    Icon: ReportsIcon,
    items: [
      {
        id: "reports",
        to: "/reports",
        // GT-REPORTS-2-PHASE1B-NAV-LABEL — Owner smoke feedback: the entry
        // page IS the Report Center (gallery home), so the nav item says so
        // (was "General Reports"). Route/icon/permissions unchanged.
        labelKey: "nav.report_center",
        Icon: ReportsIcon,
        exact: true,
      },
      // GT-REPORTS-2-PHASE1B-1 (Phase-0 plan §2 IA) — export history and
      // schedules become first-class nav items instead of being reachable
      // only via in-page links. Existing routes/pages/permissions unchanged.
      {
        id: "reports-history",
        to: "/reports/history",
        labelKey: "nav.reports_history",
        Icon: ReportsIcon,
        exact: true,
      },
      {
        id: "reports-schedules",
        to: "/reports/schedules",
        labelKey: "nav.reports_schedules",
        Icon: ReportsIcon,
      },
    ],
  },
  {
    id: "integrations",
    labelKey: "nav.group.integrations",
    Icon: PlugIcon,
    items: [
      // Telegram is a channel connector (separated from notification settings). It is
      // configured on the preferences page today; a dedicated route is a future split.
      // Paket 003707: the entry jumps straight to the Telegram card via an in-app
      // anchor (`#telegram`) instead of landing on the top of the notifications page —
      // notification settings stay reachable from the Account menu, unchanged.
      {
        id: "telegram",
        to: "/me/notifications#telegram",
        labelKey: "nav.telegram",
        Icon: TelegramIcon,
      },
      {
        id: "whatsapp",
        kind: "preview",
        labelKey: "nav.whatsapp",
        badgeKey: "nav.badge.soon",
        Icon: PlugIcon,
      },
      {
        id: "email-smtp",
        kind: "preview",
        labelKey: "nav.email_smtp",
        badgeKey: "nav.badge.soon",
        Icon: PlugIcon,
      },
      {
        id: "webhooks",
        kind: "preview",
        labelKey: "nav.webhooks",
        badgeKey: "nav.badge.soon",
        Icon: PlugIcon,
      },
      {
        id: "api-integrations",
        kind: "preview",
        labelKey: "nav.api_integrations",
        badgeKey: "nav.badge.soon",
        Icon: PlugIcon,
      },
    ],
  },
  {
    id: "admin",
    labelKey: "nav.group.administration",
    Icon: ShieldIcon,
    adminOnly: true,
    items: [
      { id: "admin-hub", to: "/admin-hub", labelKey: "nav.admin", Icon: ShieldIcon, exact: true },
      { id: "admin-hub-roles", to: "/admin-hub/roles", labelKey: "nav.rbac", Icon: RbacIcon },
      {
        id: "admin-hub-archived",
        to: "/admin-hub/archived",
        labelKey: "nav.lifecycle.archived",
        Icon: ArchiveIcon,
      },
    ],
  },
];

/**
 * Standalone footer items (all roles) rendered at the bottom of the sidebar. Support
 * has no clean domain home but must stay reachable (owner: preserve every nav target).
 *
 * Paket 003707: the App Store / Google Play badges join Support here — store links are
 * not tied to any domain group or to Administration, and must stay visible for every
 * role (viewer, dispatcher, admin). Both are honest `external` out-links (new tab); the
 * `ascAppId` in the App Store URL matches `apps/ios/eas.json`.
 */
export const FOOTER_ITEMS: ReadonlyArray<NavItem> = [
  { id: "support", to: "/support", labelKey: "nav.support", Icon: SupportIcon },
  {
    id: "app-store",
    kind: "external",
    href: "https://apps.apple.com/app/id6770167272",
    labelKey: "nav.app_store",
    Icon: AppStoreIcon,
    badgeImage: { src: "/brand/app-store-badge.svg" },
  },
  {
    id: "google-play",
    kind: "external",
    href: "https://play.google.com/store/apps/details?id=com.geniustracker.app",
    labelKey: "nav.google_play",
    Icon: GooglePlayIcon,
    badgeImage: { src: "/brand/google-play-badge.svg" },
  },
];

/**
 * Mobile bottom navigation — the pinned daily cockpit (Genius · Dashboard · Map ·
 * Trips) + a "More" trigger (handled in the component) that opens the drawer with the
 * full grouped nav. Vehicles and Alerts move into the drawer (Fleet / Monitoring) and
 * stay reachable — they are no longer bottom-pinned (owner-approved mobile concept).
 */
export const MOBILE_BOTTOM_ITEMS: ReadonlyArray<BottomNavItem> = [
  {
    id: "intelligence",
    to: "/intelligence",
    labelKey: "nav.genius_intelligence_short",
    Icon: IntelligenceIcon,
  },
  { id: "dashboard", to: "/dashboard", labelKey: "nav.dashboard", Icon: DashboardIcon },
  { id: "map", to: "/map", labelKey: "nav.map", Icon: MapPinIcon },
  { id: "trips", to: "/trips", labelKey: "nav.trips", Icon: RouteIcon },
];
